// Error types and categories
export const ErrorTypes = {
    // Authentication errors
    AUTH_INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
    AUTH_TOKEN_EXPIRED: 'AUTH_TOKEN_EXPIRED',
    AUTH_TOKEN_INVALID: 'AUTH_TOKEN_INVALID',
    AUTH_RATE_LIMITED: 'AUTH_RATE_LIMITED',
    AUTH_NETWORK_ERROR: 'AUTH_NETWORK_ERROR',
    AUTH_SERVER_ERROR: 'AUTH_SERVER_ERROR',
    AUTH_OAUTH_ERROR: 'AUTH_OAUTH_ERROR',
    
    // Validation errors
    VALIDATION_EMAIL_INVALID: 'VALIDATION_EMAIL_INVALID',
    VALIDATION_PASSWORD_WEAK: 'VALIDATION_PASSWORD_WEAK',
    VALIDATION_INPUT_SANITIZED: 'VALIDATION_INPUT_SANITIZED',
    
    // Session errors
    SESSION_EXPIRED: 'SESSION_EXPIRED',
    SESSION_REFRESH_FAILED: 'SESSION_REFRESH_FAILED',
    SESSION_RESTORE_FAILED: 'SESSION_RESTORE_FAILED',
    
    // Security errors
    SECURITY_CSRF_INVALID: 'SECURITY_CSRF_INVALID',
    SECURITY_RATE_LIMITED: 'SECURITY_RATE_LIMITED',
    SECURITY_TOKEN_DECRYPTION_FAILED: 'SECURITY_TOKEN_DECRYPTION_FAILED',
    
    // Network errors
    NETWORK_TIMEOUT: 'NETWORK_TIMEOUT',
    NETWORK_CONNECTION_LOST: 'NETWORK_CONNECTION_LOST',
    NETWORK_SERVER_UNAVAILABLE: 'NETWORK_SERVER_UNAVAILABLE',
    
    // System errors
    SYSTEM_UNKNOWN: 'SYSTEM_UNKNOWN',
    SYSTEM_STORAGE_ERROR: 'SYSTEM_STORAGE_ERROR',
    SYSTEM_PROCESS_ERROR: 'SYSTEM_PROCESS_ERROR'
};

// Error severity levels
export const ErrorSeverity = {
    LOW: 'LOW',
    MEDIUM: 'MEDIUM',
    HIGH: 'HIGH',
    CRITICAL: 'CRITICAL'
};

// User-friendly error messages
export const ErrorMessages = {
    [ErrorTypes.AUTH_INVALID_CREDENTIALS]: {
        message: 'Invalid email or password. Please check your credentials and try again.',
        severity: ErrorSeverity.MEDIUM,
        userAction: 'Please verify your email and password.'
    },
    [ErrorTypes.AUTH_TOKEN_EXPIRED]: {
        message: 'Your session has expired. Please log in again.',
        severity: ErrorSeverity.MEDIUM,
        userAction: 'Please log in to continue.'
    },
    [ErrorTypes.AUTH_TOKEN_INVALID]: {
        message: 'Your session is invalid. Please log in again.',
        severity: ErrorSeverity.MEDIUM,
        userAction: 'Please log in to continue.'
    },
    [ErrorTypes.AUTH_RATE_LIMITED]: {
        message: 'Too many login attempts. Please wait before trying again.',
        severity: ErrorSeverity.MEDIUM,
        userAction: 'Please wait a few minutes before trying again.'
    },
    [ErrorTypes.AUTH_NETWORK_ERROR]: {
        message: 'Network connection error. Please check your internet connection.',
        severity: ErrorSeverity.MEDIUM,
        userAction: 'Please check your internet connection and try again.'
    },
    [ErrorTypes.AUTH_SERVER_ERROR]: {
        message: 'Server error. Please try again later.',
        severity: ErrorSeverity.HIGH,
        userAction: 'Please try again in a few minutes.'
    },
    [ErrorTypes.AUTH_OAUTH_ERROR]: {
        message: 'Google sign-in error. Please try again.',
        severity: ErrorSeverity.MEDIUM,
        userAction: 'Please try signing in with Google again.'
    },
    [ErrorTypes.VALIDATION_EMAIL_INVALID]: {
        message: 'Please enter a valid email address.',
        severity: ErrorSeverity.LOW,
        userAction: 'Please check your email format.'
    },
    [ErrorTypes.VALIDATION_PASSWORD_WEAK]: {
        message: 'Password must be at least 8 characters with uppercase, lowercase, numbers, and special characters.',
        severity: ErrorSeverity.LOW,
        userAction: 'Please choose a stronger password.'
    },
    [ErrorTypes.SESSION_EXPIRED]: {
        message: 'Your session has expired due to inactivity.',
        severity: ErrorSeverity.MEDIUM,
        userAction: 'Please log in again to continue.'
    },
    [ErrorTypes.SESSION_REFRESH_FAILED]: {
        message: 'Unable to refresh your session. Please log in again.',
        severity: ErrorSeverity.MEDIUM,
        userAction: 'Please log in to continue.'
    },
    [ErrorTypes.SECURITY_CSRF_INVALID]: {
        message: 'Security token missing. Please refresh the page and try again.',
        severity: ErrorSeverity.HIGH,
        userAction: 'Please refresh the page and try again.'
    },
    [ErrorTypes.NETWORK_TIMEOUT]: {
        message: 'Request timed out. Please check your connection and try again.',
        severity: ErrorSeverity.MEDIUM,
        userAction: 'Please check your internet connection.'
    },
    [ErrorTypes.SYSTEM_UNKNOWN]: {
        message: 'An unexpected error occurred. Please try again.',
        severity: ErrorSeverity.HIGH,
        userAction: 'Please try again or contact support if the problem persists.'
    }
};

// Error handler class
export class ErrorHandler {
    constructor() {
        this.errorCount = 0;
        this.errorHistory = [];
        this.maxErrorHistory = 100;
    }

    // Handle and categorize errors
    handleError(error, context = {}) {
        const errorInfo = this.categorizeError(error);
        const userMessage = this.getUserMessage(errorInfo.type);
        
        // Log the error
        this.logError(errorInfo, context);
        
        // Track error for analytics
        this.trackError(errorInfo);
        
        return {
            type: errorInfo.type,
            message: userMessage.message,
            severity: userMessage.severity,
            userAction: userMessage.userAction,
            technical: errorInfo.technical,
            timestamp: new Date().toISOString()
        };
    }

    // Categorize errors based on error message and type
    categorizeError(error) {
        const errorMessage = error.message?.toLowerCase() || '';
        const errorCode = error.code || '';
        
        // Authentication errors
        if (errorMessage.includes('invalid login credentials') || 
            errorMessage.includes('invalid email or password')) {
            return { type: ErrorTypes.AUTH_INVALID_CREDENTIALS, technical: error };
        }
        
        if (errorMessage.includes('jwt expired') || 
            errorMessage.includes('token expired')) {
            return { type: ErrorTypes.AUTH_TOKEN_EXPIRED, technical: error };
        }
        
        if (errorMessage.includes('invalid jwt') || 
            errorMessage.includes('invalid token')) {
            return { type: ErrorTypes.AUTH_TOKEN_INVALID, technical: error };
        }
        
        if (errorMessage.includes('too many requests') || 
            errorMessage.includes('rate limited')) {
            return { type: ErrorTypes.AUTH_RATE_LIMITED, technical: error };
        }
        
        if (errorMessage.includes('network') || 
            errorMessage.includes('connection') ||
            errorCode === 'NETWORK_ERROR') {
            return { type: ErrorTypes.AUTH_NETWORK_ERROR, technical: error };
        }
        
        if (errorMessage.includes('oauth') || 
            errorMessage.includes('google')) {
            return { type: ErrorTypes.AUTH_OAUTH_ERROR, technical: error };
        }
        
        // Validation errors
        if (errorMessage.includes('email') && 
            (errorMessage.includes('invalid') || errorMessage.includes('format'))) {
            return { type: ErrorTypes.VALIDATION_EMAIL_INVALID, technical: error };
        }
        
        if (errorMessage.includes('password') && 
            (errorMessage.includes('weak') || errorMessage.includes('requirements'))) {
            return { type: ErrorTypes.VALIDATION_PASSWORD_WEAK, technical: error };
        }
        
        // Session errors
        if (errorMessage.includes('session expired') || 
            errorMessage.includes('session timeout')) {
            return { type: ErrorTypes.SESSION_EXPIRED, technical: error };
        }
        
        if (errorMessage.includes('refresh failed') || 
            errorMessage.includes('token refresh')) {
            return { type: ErrorTypes.SESSION_REFRESH_FAILED, technical: error };
        }
        
        // Security errors
        if (errorMessage.includes('csrf') || 
            errorMessage.includes('security token')) {
            return { type: ErrorTypes.SECURITY_CSRF_INVALID, technical: error };
        }
        
        // Network errors
        if (errorMessage.includes('timeout') || 
            errorCode === 'TIMEOUT') {
            return { type: ErrorTypes.NETWORK_TIMEOUT, technical: error };
        }
        
        // Default to unknown error
        return { type: ErrorTypes.SYSTEM_UNKNOWN, technical: error };
    }

    // Get user-friendly message
    getUserMessage(errorType) {
        return ErrorMessages[errorType] || ErrorMessages[ErrorTypes.SYSTEM_UNKNOWN];
    }

    // Log error for debugging
    logError(errorInfo, context) {
        const logEntry = {
            timestamp: new Date().toISOString(),
            type: errorInfo.type,
            message: errorInfo.technical.message,
            stack: errorInfo.technical.stack,
            context: context,
            userAgent: navigator.userAgent,
            url: window.location.href
        };
        
        console.error('Auth Error:', logEntry);
        
        // Store in error history
        this.errorHistory.push(logEntry);
        if (this.errorHistory.length > this.maxErrorHistory) {
            this.errorHistory.shift();
        }
    }

    // Track error for analytics
    trackError(errorInfo) {
        this.errorCount++;
        
        // In production, this would send to analytics service
        if (process.env.NODE_ENV === 'production') {
            // Example: send to error tracking service
            // analytics.track('auth_error', {
            //     type: errorInfo.type,
            //     count: this.errorCount
            // });
        }
    }

    // Get error statistics
    getErrorStats() {
        const stats = {};
        this.errorHistory.forEach(entry => {
            stats[entry.type] = (stats[entry.type] || 0) + 1;
        });
        return stats;
    }

    // Clear error history
    clearErrorHistory() {
        this.errorHistory = [];
        this.errorCount = 0;
    }
}

// Global error handler instance
export const errorHandler = new ErrorHandler();
